(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealRecords = new WeakMap();
  const pointerRecords = new WeakMap();
  const depthItems = new Set();
  let depthFrame = 0;
  let depthListening = false;

  const findAll = (root, selector) => {
    const matches = root.matches?.(selector) ? [root] : [];
    return matches.concat([...root.querySelectorAll?.(selector) || []]);
  };

  const revealSelector = [
    '[data-ilb-reveal]',
    '[data-ilb-reveal-word]',
    '.ilb-shot-ball__purchase-rail > *',
    '.ilb-shot-ball__scroll-copy > *',
    '.ilb-b__scroll-reticle',
    '.ilb-shot-ball__anatomy-copy > *',
    '.ilb-shot-ball__anatomy-point',
    '.ilb-shot-ball__section-head > *',
    '.ilb-shot-ball__signal',
    '.ilb-shot-ball__audience-head > *',
    '.ilb-shot-ball__audience-item',
    '.ilb-shot-ball__film-copy > *',
    '.ilb-shot-ball__details-intro > *',
    '.ilb-shot-ball__details-row',
    '.ilb-shot-ball__faq-grid > *',
    '.ilb-shot-ball__faq-list details',
    '.ilb-shot-ball__final > div > *'
  ].join(',');

  const reveal = (item) => {
    item.classList.add('is-ilb-revealed');
    const record = revealRecords.get(item);
    if (record?.observer) record.observer.unobserve(item);
  };

  const initReveal = (item, index) => {
    if (revealRecords.has(item)) return;
    item.dataset.ilbReveal = '';
    item.style.setProperty('--ilb-reveal-delay', `${Math.min((index % 5) * 70, 280)}ms`);

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealRecords.set(item, { observer: null });
      reveal(item);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });

    revealRecords.set(item, { observer });
    observer.observe(item);
  };

  const initPointerGlow = (item) => {
    if (pointerRecords.has(item) || reduceMotion.matches || !window.matchMedia('(pointer: fine)').matches) return;
    const controller = new AbortController();
    const { signal } = controller;
    let frame = 0;
    let pending = null;

    const paint = () => {
      frame = 0;
      if (!pending) return;
      const rect = item.getBoundingClientRect();
      const x = ((pending.clientX - rect.left) / Math.max(rect.width, 1)) * 100;
      const y = ((pending.clientY - rect.top) / Math.max(rect.height, 1)) * 100;
      item.style.setProperty('--ilb-pointer-x', `${Math.max(0, Math.min(100, x))}%`);
      item.style.setProperty('--ilb-pointer-y', `${Math.max(0, Math.min(100, y))}%`);
      item.classList.add('has-ilb-pointer');
    };

    item.addEventListener('pointermove', (event) => {
      pending = event;
      if (!frame) frame = window.requestAnimationFrame(paint);
    }, { passive: true, signal });
    item.addEventListener('pointerleave', () => item.classList.remove('has-ilb-pointer'), { signal });

    pointerRecords.set(item, {
      destroy() {
        controller.abort();
        if (frame) window.cancelAnimationFrame(frame);
        item.classList.remove('has-ilb-pointer');
        item.style.removeProperty('--ilb-pointer-x');
        item.style.removeProperty('--ilb-pointer-y');
        pointerRecords.delete(item);
      }
    });
  };

  const paintDepth = () => {
    depthFrame = 0;
    const viewport = window.innerHeight || 1;
    depthItems.forEach((item) => {
      if (!item.isConnected) {
        depthItems.delete(item);
        return;
      }
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -viewport * 0.25 || rect.top > viewport * 1.25) return;
      const center = rect.top + rect.height / 2;
      const normalized = Math.max(-1, Math.min(1, (viewport / 2 - center) / viewport));
      const strength = item.dataset.ilbDepth === 'hero' ? 18 : 28;
      item.style.setProperty('--ilb-depth-y', `${(normalized * strength).toFixed(2)}px`);
      item.style.setProperty('--ilb-depth-turn', `${(normalized * 0.7).toFixed(2)}deg`);
    });
  };

  const scheduleDepth = () => {
    if (!depthFrame) depthFrame = window.requestAnimationFrame(paintDepth);
  };

  const startDepth = () => {
    if (depthListening || reduceMotion.matches || depthItems.size === 0) return;
    depthListening = true;
    window.addEventListener('scroll', scheduleDepth, { passive: true });
    window.addEventListener('resize', scheduleDepth, { passive: true });
    scheduleDepth();
  };

  const pauseDepth = () => {
    if (!depthListening) return;
    depthListening = false;
    window.removeEventListener('scroll', scheduleDepth);
    window.removeEventListener('resize', scheduleDepth);
    if (depthFrame) window.cancelAnimationFrame(depthFrame);
    depthFrame = 0;
  };

  const stopDepth = () => {
    if (depthItems.size) return;
    pauseDepth();
  };

  const initWithin = (root = document) => {
    document.documentElement.classList.add('ilb-b-motion-ready');
    findAll(root, revealSelector).forEach(initReveal);
    findAll(root, '[data-ilb-pointer-glow], .ilb-shot-ball__reader, .ilb-shot-ball__anatomy-media').forEach(initPointerGlow);
    findAll(root, '[data-ilb-depth]').forEach((item) => depthItems.add(item));
    startDepth();
  };

  const destroyWithin = (root) => {
    findAll(root, '[data-ilb-reveal]').forEach((item) => {
      revealRecords.get(item)?.observer?.disconnect();
      revealRecords.delete(item);
      item.classList.remove('is-ilb-revealed');
      item.removeAttribute('data-ilb-reveal');
      item.style.removeProperty('--ilb-reveal-delay');
    });
    findAll(root, '[data-ilb-pointer-glow], .ilb-shot-ball__reader, .ilb-shot-ball__anatomy-media').forEach((item) => pointerRecords.get(item)?.destroy());
    findAll(root, '[data-ilb-depth]').forEach((item) => depthItems.delete(item));
    stopDepth();
  };

  reduceMotion.addEventListener?.('change', () => {
    if (reduceMotion.matches) {
      document.querySelectorAll('[data-ilb-reveal]').forEach(reveal);
      document.querySelectorAll('[data-ilb-pointer-glow], .ilb-shot-ball__reader, .ilb-shot-ball__anatomy-media')
        .forEach((item) => pointerRecords.get(item)?.destroy());
      pauseDepth();
      depthItems.forEach((item) => {
        item.style.removeProperty('--ilb-depth-y');
        item.style.removeProperty('--ilb-depth-turn');
      });
    } else {
      initWithin();
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initWithin(), { once: true });
  else initWithin();

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
